import React from 'react';
import LegalPage from './LegalPage';

const Terms: React.FC = () => (
  <LegalPage title="Terms of Service" updated="August 22, 2026">
    <p>
      These terms govern your use of Clevor. By creating an account you agree to them. Please read
      the sections on AI output, bid responsibility, and dispute resolution — they affect what you
      can rely on and how disagreements are resolved.
    </p>

    <h2>1. Who we are</h2>
    <p>
      "Clevor", "we" and "us" refer to the operator of the Clevor service, currently operating as
      an unincorporated business. We intend to incorporate in Delaware, and these terms will be
      assigned to that entity on formation. "You" refers to the person or organization using the
      service.
    </p>

    <h2>2. The service</h2>
    <p>
      Clevor helps small businesses find, evaluate and respond to U.S. federal contracting
      opportunities. It retrieves public solicitation data, analyses requirements, checks
      eligibility signals, suggests potential teaming partners, and drafts proposal content.
    </p>

    <h2>3. Beta status</h2>
    <p>
      Clevor is in private beta. Features may change, break, or be removed. Access is granted at
      our discretion and may be modified or withdrawn. We aim for reliability but do not offer an
      uptime commitment during beta.
    </p>

    <h2>4. Your account</h2>
    <ul>
      <li>You must provide accurate information and keep it current.</li>
      <li>You are responsible for activity under your account and for keeping your password secure.</li>
      <li>You must be 18 or older and authorized to act for the business you represent.</li>
      <li>Do not share credentials. Team access is provided through team features where available.</li>
    </ul>

    <h2>5. AI-generated output — read this</h2>
    <p>
      Clevor uses AI to analyse solicitations and draft proposal content. AI output can be
      incomplete, out of date, or wrong, including in ways that read as confident and specific.
    </p>
    <ul>
      <li><strong>Everything Clevor produces is a draft.</strong> You are responsible for reviewing it before relying on it or submitting it.</li>
      <li><strong>Nothing Clevor produces is legal, financial, or contracting advice.</strong> Compliance findings, eligibility checks and set-aside assessments are informational signals, not determinations.</li>
      <li><strong>Verify eligibility yourself.</strong> Clevor cannot read your SAM.gov registration and cannot confirm your size status, certifications, or registration currency. Those determinations rest with you, SBA, and the contracting officer.</li>
      <li><strong>Verify vendors yourself.</strong> Suggested teaming partners are drawn from public registration data. Inclusion is not a recommendation, endorsement, or statement that a firm is qualified, available, or in good standing.</li>
      <li><strong>You own the submission.</strong> Clevor does not submit bids on your behalf and is not a party to any offer you make.</li>
    </ul>

    <h2>6. Your content</h2>
    <p>
      You keep ownership of everything you upload and of the proposals you produce. You grant us
      the limited licence needed to operate the service — to store your content, process it, and
      transmit it to the third-party services described in our{' '}
      <a href="/privacy">Privacy Policy</a>. We do not claim ownership of your work product and do
      not use your content to train our own models.
    </p>

    <h2>7. Acceptable use</h2>
    <ul>
      <li>Do not use Clevor to prepare or submit materially false statements, fabricated past performance, or invented references to any government agency.</li>
      <li>Do not upload classified information, or controlled unclassified information requiring safeguards we have not agreed to in writing.</li>
      <li>Do not attempt to breach, overload, scrape, or reverse-engineer the service.</li>
      <li>Do not resell or redistribute Clevor's output as a competing service.</li>
      <li>Do not use Clevor in violation of federal procurement law or regulation.</li>
    </ul>
    <p>
      We may suspend or terminate accounts that violate these terms. The first item matters most:
      submitting fabricated information to the federal government carries serious consequences for
      you, and Clevor's drafting features do not change that.
    </p>

    <h2>8. Plans, billing and beta access</h2>
    <p>
      Paid plans and self-serve checkout are currently disabled. Beta access is granted manually
      and free of charge. When billing is enabled, prices, billing periods and refund terms will
      be presented before you are charged, and these terms will be updated. Payment processing is
      handled by Stripe; we do not store card details.
    </p>

    <h2>9. Availability and changes</h2>
    <p>
      We may change, suspend or discontinue any part of the service. We will give reasonable
      notice of material changes to paying customers where we can. We may update these terms; if
      changes are material we will notify account holders by email and revise the date above.
    </p>

    <h2>10. Termination</h2>
    <p>
      You may stop using Clevor and delete your account at any time from your account settings. We
      may suspend or terminate access for violation of these terms, for legal reasons, or if we
      discontinue the service. Export your data before deleting your account — deletion is
      permanent.
    </p>

    <h2>11. Disclaimers</h2>
    <p>
      The service is provided "as is" and "as available", without warranties of any kind, express
      or implied, including merchantability, fitness for a particular purpose, and
      non-infringement. We do not warrant that the service will be uninterrupted, error-free, or
      that any output will be accurate, complete, or result in a contract award.
    </p>

    <h2>12. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, Clevor is not liable for indirect, incidental,
      special, consequential or punitive damages, or for lost profits, lost contracts, lost bids,
      or lost business opportunities, arising from your use of the service. Our total liability for
      any claim is limited to the greater of the amount you paid us in the twelve months before the
      claim, or one hundred U.S. dollars. During the beta, when the service is provided free of
      charge, that limit is one hundred U.S. dollars.
    </p>

    <h2>13. Indemnification</h2>
    <p>
      You agree to indemnify and hold Clevor harmless from claims arising out of your use of the
      service, your content, your bids and submissions, and your violation of these terms or of
      applicable law.
    </p>

    <h2>14. Dispute resolution — arbitration and class-action waiver</h2>
    <p>
      <strong>Please read this section carefully. It affects your legal rights.</strong>
    </p>
    <p>
      Any dispute arising out of or relating to these terms or the service will be resolved by
      binding individual arbitration, rather than in court, except that either party may bring an
      individual claim in small-claims court, and either party may seek injunctive relief in court
      for infringement or misuse of intellectual property.
    </p>
    <p>
      Arbitration will be administered under the rules of a recognized arbitration provider, seated
      in Delaware, and conducted in English by a single arbitrator. Each party is responsible for
      its own fees except as the applicable rules provide otherwise.
    </p>
    <p>
      <strong>Class-action waiver.</strong> Disputes will be brought only in an individual
      capacity. You and Clevor each waive any right to bring or participate in a class,
      consolidated, or representative action. If this waiver is found unenforceable as to a
      particular claim, that claim will proceed in court and the remainder of this section will
      continue to apply.
    </p>
    <p>
      <strong>Opt out.</strong> You may opt out of this arbitration agreement by emailing{' '}
      <a href="mailto:support@clevor.ai">support@clevor.ai</a> within 30 days of first accepting
      these terms, stating your name and that you are opting out. Opting out does not affect any
      other part of these terms.
    </p>

    <h2>15. Governing law</h2>
    <p>
      These terms are governed by the laws of the State of Delaware, without regard to its
      conflict-of-laws rules.
    </p>

    <h2>16. General</h2>
    <p>
      If any provision is found unenforceable, the rest remains in effect. Our failure to enforce a
      provision is not a waiver of it. You may not assign these terms without our consent; we may
      assign them, including to the Delaware entity referenced in section 1 or in connection with a
      merger or acquisition. These terms, together with the Privacy Policy, are the entire
      agreement between us regarding the service.
    </p>

    <h2>17. Contact</h2>
    <p>
      <a href="mailto:support@clevor.ai">support@clevor.ai</a>
    </p>
  </LegalPage>
);

export default Terms;
